import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getGetProfileQueryKey,
  useGetProfile,
} from '@biasafe.queries/user/user';
import { useTranslation } from 'react-i18next';
import { useFirmFavicon } from '@/shared/hooks/useFirmFavicon';
import { useOrganization } from '@/features/Team/queries';
import { PROFILE_STALE_TIME } from '@/shared/config/queryDefaults';
import { INVITE_TOKEN_KEY } from '@/features/Auth/views/AcceptInvitationPage';
import apiClient from '@/api';
import { usePreferencesStore } from '@/entities/preferencesStore';
import { Sidebar } from '@/features/Sidebar/views/Sidebar';
import { Header } from '@/features/Header/Header';
import { cn } from '@/lib/utils';
import { ScreenLoader } from '@/shared/components/ScreenLoader';
import { useUserStore } from '@/entities/user/store';
import { useGcpSessionSync } from '@/features/Auth/hooks/useGcpSessionSync';
import { useIdleSignOut } from '@/features/Auth/hooks/useIdleSignOut';
import { FeatureErrorBoundary } from '@/shared/components/FeatureErrorBoundary';
import { SoteriaSidePanel } from '@/features/SoteriaAI/components/SoteriaSidePanel';
import { useAccountAppearance } from '@/shared/hooks/useAccountAppearance';
import { SecureAccountBanner } from '@/features/Auth/components/SecureAccountBanner';
import { ReviewAccessBanner } from '@/features/Team/components/ReviewAccessBanner';
import { reviewAccessEndedMessage } from '@/features/Team/reviewAccess';
import { PendingInvitesRelease } from '@/features/Team/usePendingInvites';
import { WelcomeTour } from '@/features/WelcomeTour/WelcomeTour';
import { ConnectToolsStep } from '@/features/Onboarding/ConnectToolsStep';

export const AuthLayout = () => {
  const { i18n } = useTranslation();
  // Appearance follows the account, and this is the first place that is only
  // ever rendered for a signed-in user — on /login there is no profile to ask
  // for. See the hook for why localStorage stays as a cache underneath it.
  useAccountAppearance();
  const organization = useOrganization();
  useFirmFavicon(organization.data?.orgMetadata?.firm_favicon_data_url);

  // Auth check handled by beforeLoad in roots.ts — this component
  // only renders when the user is already authenticated.
  const navigate = useNavigate();
  const isCollapsed = usePreferencesStore((state) => state.isCollapsed);
  // When the Soteria drawer is open we force the LEFT sidebar to its
  // collapsed presentation (without mutating the user's preference) so
  // there's more room for the page + drawer side-by-side. The user's
  // own collapse pref is restored as soon as the drawer closes.
  const logout = useUserStore((state) => state.logout);
  const setUser = useUserStore((state) => state.setUser);

  // GCP auth: keep the session cookie in sync with Firebase's rotating ID
  // token so sessions survive past the ~1h token expiry.
  useGcpSessionSync();

  // ...and end them when nobody is using them. That rotation is exactly why a
  // signed-in tab never expired on its own: Firebase's refresh token has no
  // expiry, so the pair above would keep a session alive overnight, over a
  // weekend, on a shared machine. See useIdleSignOut.
  useIdleSignOut();

  // Validate session on mount — if the cookie is expired or the user was
  // deleted server-side, the profile fetch will fail and we log out.
  const {
    data: profile,
    error: profileError,
    isError,
    isLoading,
  } = useGetProfile({
    query: {
      queryKey: getGetProfileQueryKey(),
      retry: false,
      staleTime: PROFILE_STALE_TIME,
    },
  });

  useEffect(() => {
    if (isError) {
      // A reviewer whose access expired or was revoked is refused on every
      // request, this one included. Say so on the way out instead of dropping
      // them on a login page with no explanation.
      const ended = reviewAccessEndedMessage(profileError);
      if (ended) toast.error(ended, { duration: 10000 });
      logout();
      navigate({ to: '/login', replace: true });
    }
  }, [isError, profileError, logout, navigate]);

  useEffect(() => {
    if (profile) {
      setUser(profile);
      void i18n.changeLanguage(profile.locale === 'fr' ? 'fr' : 'en');
    }
  }, [profile, setUser]);

  // Complete a pending team invitation once the user is authenticated (they
  // arrived via an accept link, then signed up). Accepting moves them into the
  // inviting org, so we refetch the profile afterwards. Runs before the
  // onboarding redirect below, which is gated while this resolves so an invited
  // user isn't bounced to onboarding.
  const queryClient = useQueryClient();
  const [inviteResolving, setInviteResolving] = useState(
    () => !!sessionStorage.getItem(INVITE_TOKEN_KEY),
  );
  const inviteRan = useRef(false);
  useEffect(() => {
    const token = sessionStorage.getItem(INVITE_TOKEN_KEY);
    if (!token || !profile || inviteRan.current) return;
    inviteRan.current = true;
    void apiClient
      .post('/team/invitations/accept', { token })
      .catch(() => undefined) // failure → they fall through to normal onboarding
      .finally(() => {
        sessionStorage.removeItem(INVITE_TOKEN_KEY);
        queryClient.removeQueries({ queryKey: getGetProfileQueryKey() });
        setInviteResolving(false);
      });
  }, [profile, queryClient]);

  // Onboarding is normally a post-signup step — the signup → verify
  // → login flow sets a sessionStorage flag that the login mutation
  // honours to route through /onboarding once. But that flag only
  // covers the signup-followed-immediately-by-login path. A user who
  // signed up, abandoned mid-onboarding, then came back the next day
  // and signed in still has `local_org_id = None` and every protected
  // endpoint will 500 (organization_id is None in the JWT and the
  // backend rejects the request).
  //
  // Source of truth: the /user/me profile's needsOnboarding flag,
  // derived backend-side from `local_org_id is None`. As soon as the
  // profile fetch resolves with needsOnboarding=true, redirect to
  // /onboarding.
  const location = useLocation();
  useEffect(() => {
    if (inviteResolving) return; // let a pending invite set the org first
    if (!profile?.needsOnboarding) return;
    if (location.pathname.startsWith('/onboarding')) return;
    navigate({ to: '/onboarding', replace: true });
  }, [profile?.needsOnboarding, location.pathname, navigate, inviteResolving]);

  // Gate the layout chrome on a settled profile fetch.
  //
  // Previously the layout rendered immediately whenever a `cachedUser`
  // existed in the persisted zustand store, on the bet that the cache
  // matched the fresh profile. That bet was wrong for the post-signup
  // case: the cached user from a previous session has
  // `needs_onboarding=false`, the cached layout renders /strategies,
  // and then the profile fetch resolves a few hundred ms later with
  // `needs_onboarding=true` and the redirect fires — the user sees a
  // flash of /strategies before being kicked to /onboarding. Worse,
  // if the profile fetch takes longer than the first protected query,
  // that protected query 401s on the missing organization_id claim and
  // the api.ts interceptor logs them out.
  //
  // Now: if the profile is still loading OR the freshly-fetched
  // profile says onboarding is required, hold the spinner. Once
  // profile.needsOnboarding === false, render the chrome.
  if (isLoading || inviteResolving) {
    return <ScreenLoader text="Loading..." className="h-screen" />;
  }
  if (profile?.needsOnboarding) {
    // The redirect effect will fire on the next tick; show the spinner
    // in the meantime so the user doesn't see an empty AuthLayout.
    return <ScreenLoader text="Loading..." className="h-screen" />;
  }

  return (
    <>
      <Sidebar />
      {/* Clip without making the shell scrollable: descendant focus/anchor
          navigation must only move the content below the persistent header. */}
      <main
        className={cn(
          'flex h-screen flex-col overflow-clip transition-[margin-left] duration-300 ease-out',
          // Left margin matches the Figma Sidebar widths exactly: 56px
          // collapsed, 220px expanded.
          //
          // THE DRAWER NO LONGER FORCES IT. It read `isCollapsed ||
          // isSoteriaOpen`, so opening the assistant also retreated the
          // sidebar — "in lockstep with the drawer", said the comment. That
          // is a second thing moving that nobody asked to move, and with the
          // header now static there is no lockstep left to keep. The sidebar
          // holds whatever state its own control was given.
          isCollapsed ? 'ml-[56px]' : 'ml-[220px]',
        )}
      >
        {/* THE HEADER SPANS, and everything below it is the split.
            
            A right margin used to sit on THIS element, which renders the header
            as its first child — so opening the assistant shrank the header
            along with the page under it, while a `fixed` drawer crossed over
            it on its way down the screen. Two things read as an overlay
            because they genuinely overlapped.
            
            The margin is gone rather than moved: see the row below, where the
            page and the assistant are two columns of one flex line. The header
            is outside that row, so nothing the assistant does can reach it. */}
        <Header />
        <SecureAccountBanner />
        <ReviewAccessBanner />
        <PendingInvitesRelease />
        {/* Local dev only — renders nothing in a production bundle. Sample data
            is fabricated, so it is never seeded automatically; this is the only
            way to get it, and the API refuses it outside local dev regardless. */}
        {/* THE SPLIT, AS A ROW. The page and the assistant are two columns of
            one flex line, so the panel takes its width out of the row rather
            than being laid over it.
            
            It was a `fixed` drawer plus a matching `mr-[…]` on this content —
            two constants that had to agree for the page to be pushed instead
            of covered, and they did not. With the drawer open a card's right
            border was missing and a long question read "…stock exchange itself
            or O", clipped mid-word underneath it.
            
            `min-h-0` on the row and `min-w-0` on the column are the two that
            make it hold: without the first the row cannot be shorter than its
            content and the page stops scrolling, and without the second a long
            unbroken line pushes the row wider than the window — which is the
            other half of why text ran under the panel. */}
        <div className="flex min-h-0 flex-1">
          <div className="min-w-0 flex-1 overflow-y-auto overscroll-contain no-scrollbar bg-background">
            {/* resetKey: navigating away clears a caught error, so one route
                that threw does not leave every later route showing its
                fallback. */}
            <FeatureErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </FeatureErrorBoundary>
          </div>
          {/* Soteria — a column of the layout, mounted here rather than beside
              it so the two genuinely share the line. The Sparkles button in
              the header still opens it from any route; the store is what they
              share, not a portal. */}
          <SoteriaSidePanel />
        </div>
      </main>
      {/* Here and not higher: this is past both spinners above, so the tour
          can only open over the real app — never over the onboarding redirect,
          and never before the profile that decides whether to show it. */}
      <WelcomeTour profile={profile} />
      {/* After the tour, never over it: the step waits for the tour's flag
          to clear. Brand-new accounts only, decided by the server. */}
      <ConnectToolsStep profile={profile} />
    </>
  );
};
